import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import AxiosClient from '@/api/client';
import { Endpoints, EndpointHelpers, replaceParams } from '@/config/endpoints';
import {
  type ApiAssessment,
  type ApiAssessmentDetail,
  type AssessmentsListParams,
  type CreateAssessmentRequest,
  type ListPaginated,
  type ItemResponse,
  type AvailableScopeResponse,
} from '@/types/api';
import { useToast } from '@/hooks/use-toast';

// ============================================================================
// Query Keys Factory
// ============================================================================

export const assessmentQueryKeys = {
  all: ['assessments'] as const,
  lists: () => [...assessmentQueryKeys.all, 'list'] as const,
  list: (params?: AssessmentsListParams) =>
    [...assessmentQueryKeys.lists(), params] as const,
  details: () => [...assessmentQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...assessmentQueryKeys.details(), id] as const,
  scopes: () => [...assessmentQueryKeys.all, 'scope'] as const,
  scope: (propertyId: number) =>
    [...assessmentQueryKeys.scopes(), propertyId] as const,
} as const;

// ============================================================================
// Assessment List Query
// ============================================================================

/**
 * Fetches paginated list of assessments with filters
 * @param params - Query parameters (page, search, property, status, date range, etc.)
 */
export function useAssessmentsQuery(
  params?: AssessmentsListParams,
  options?: Omit<
    UseQueryOptions<ListPaginated<ApiAssessment>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: assessmentQueryKeys.list(params),
    queryFn: async () => {
      const client = new AxiosClient<never, ApiAssessment>({
        endpoint: Endpoints.ASSESSMENTS_LIST,
      });

      const response = await client.getPaginated({ params });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch assessments');
      }

      return response;
    },
    ...options,
  });
}

// ============================================================================
// Assessment Detail Query
// ============================================================================

/**
 * Fetches single assessment by ID
 * @param id - Assessment ID (ASS_XXXXXXXXXX)
 */
export function useAssessmentQuery(
  id: string | undefined,
  options?: Omit<
    UseQueryOptions<ItemResponse<ApiAssessmentDetail>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: assessmentQueryKeys.detail(id!),
    queryFn: async () => {
      if (!id) throw new Error('Assessment ID is required');

      const client = new AxiosClient<never, ApiAssessmentDetail>({
        endpoint: EndpointHelpers.getAssessmentDetail(id),
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch assessment');
      }

      return response;
    },
    enabled: !!id,
    ...options,
  });
}

// ============================================================================
// Available Scope Query (for creating assessments)
// ============================================================================

/**
 * Fetches available scope for a property
 * @param propertyId - Property ID
 */
export function useAvailableScopeQuery(
  propertyId: number | undefined,
  options?: Omit<
    UseQueryOptions<ItemResponse<AvailableScopeResponse>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: assessmentQueryKeys.scope(propertyId!),
    queryFn: async () => {
      if (!propertyId) throw new Error('Property ID is required');

      const client = new AxiosClient<never, AvailableScopeResponse>({
        endpoint: replaceParams('/properties/{id}/available-scope', {
          id: propertyId,
        }),
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch available scope');
      }

      return response;
    },
    enabled: !!propertyId,
    ...options,
  });
}

// ============================================================================
// Create Assessment Mutation
// ============================================================================

interface CreateAssessmentContext {
  previousAssessments?: ListPaginated<ApiAssessment>;
}

/**
 * Creates a new assessment
 */
export function useCreateAssessmentMutation(
  options?: UseMutationOptions<
    ItemResponse<ApiAssessment>,
    Error,
    CreateAssessmentRequest,
    CreateAssessmentContext
  >
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateAssessmentRequest) => {
      const client = new AxiosClient<CreateAssessmentRequest, ApiAssessment>({
        endpoint: Endpoints.ASSESSMENT_CREATE,
      });

      const response = await client.post(data);

      if (!response.status) {
        throw new Error(response.error || 'Failed to create assessment');
      }

      return response;
    },
    onMutate: async () => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: assessmentQueryKeys.lists(),
      });

      // Get previous assessments for rollback
      const previousAssessments = queryClient.getQueryData<
        ListPaginated<ApiAssessment>
      >(assessmentQueryKeys.list());

      return { previousAssessments };
    },
    onSuccess: response => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: assessmentQueryKeys.lists() });

      toast({
        title: 'Assessment Created',
        description: 'Assessment has been created successfully.',
        variant: 'default',
      });

      options?.onSuccess?.(
        response,
        {} as CreateAssessmentRequest,
        {} as CreateAssessmentContext,
        {} as any
      );
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update
      if (context?.previousAssessments) {
        queryClient.setQueryData(
          assessmentQueryKeys.list(),
          context.previousAssessments
        );
      }

      toast({
        title: 'Error',
        description: error.message || 'Failed to create assessment',
        variant: 'destructive',
      });

      options?.onError?.(error, variables, context, {} as any);
    },
  });
}

// ============================================================================
// Delete Assessment Mutation
// ============================================================================

interface DeleteAssessmentContext {
  previousAssessments?: ListPaginated<ApiAssessment>;
}

/**
 * Deletes an assessment (only drafts can be deleted)
 */
export function useDeleteAssessmentMutation(
  options?: UseMutationOptions<
    ItemResponse<unknown>,
    Error,
    string,
    DeleteAssessmentContext
  >
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const client = new AxiosClient({
        endpoint: EndpointHelpers.deleteAssessment(id),
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.error || 'Failed to delete assessment');
      }

      return response;
    },
    onMutate: async deletedId => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: assessmentQueryKeys.lists(),
      });

      // Get previous assessments for rollback
      const previousAssessments = queryClient.getQueryData<
        ListPaginated<ApiAssessment>
      >(assessmentQueryKeys.list());

      // Optimistically update cache
      queryClient.setQueriesData<ListPaginated<ApiAssessment>>(
        { queryKey: assessmentQueryKeys.lists() },
        old => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter(assessment => assessment.id !== deletedId),
            pagination: {
              ...old.pagination,
              count: old.pagination.count - 1,
              total: (old.pagination.total || 0) - 1,
            },
          };
        }
      );

      return { previousAssessments };
    },
    onSuccess: (response, id) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: assessmentQueryKeys.lists() });
      queryClient.removeQueries({ queryKey: assessmentQueryKeys.detail(id) });

      toast({
        title: 'Assessment Deleted',
        description: 'Assessment has been deleted successfully.',
        variant: 'default',
      });

      options?.onSuccess?.(response, id, {} as DeleteAssessmentContext, {} as any);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update
      if (context?.previousAssessments) {
        queryClient.setQueryData(
          assessmentQueryKeys.list(),
          context.previousAssessments
        );
      }

      toast({
        title: 'Error',
        description: error.message || 'Failed to delete assessment',
        variant: 'destructive',
      });

      options?.onError?.(error, variables, context, {} as any);
    },
  });
}

// ============================================================================
// Cache Utilities
// ============================================================================

/**
 * Hook to access assessments cache without triggering a fetch
 */
export function useAssessmentsCache(params?: AssessmentsListParams) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<ListPaginated<ApiAssessment>>(
    assessmentQueryKeys.list(params)
  );
}

/**
 * Hook to access single assessment cache without triggering a fetch
 */
export function useAssessmentCache(id: string) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<ItemResponse<ApiAssessmentDetail>>(
    assessmentQueryKeys.detail(id)
  );
}

/**
 * Prefetch assessments list
 */
export function usePrefetchAssessments() {
  const queryClient = useQueryClient();

  return (params?: AssessmentsListParams) => {
    return queryClient.prefetchQuery({
      queryKey: assessmentQueryKeys.list(params),
      queryFn: async () => {
        const client = new AxiosClient<never, ApiAssessment>({
          endpoint: Endpoints.ASSESSMENTS_LIST,
        });

        const response = await client.getPaginated({ params });

        if (!response.status) {
          throw new Error(response.error || 'Failed to fetch assessments');
        }

        return response;
      },
    });
  };
}

/**
 * Prefetch single assessment
 */
export function usePrefetchAssessment() {
  const queryClient = useQueryClient();

  return (id: string) => {
    return queryClient.prefetchQuery({
      queryKey: assessmentQueryKeys.detail(id),
      queryFn: async () => {
        const client = new AxiosClient<never, ApiAssessmentDetail>({
          endpoint: EndpointHelpers.getAssessmentDetail(id),
        });

        const response = await client.getItem();

        if (!response.status) {
          throw new Error(response.error || 'Failed to fetch assessment');
        }

        return response;
      },
    });
  };
}
